import { FINAL_IMAGE_WIDTH, FINAL_IMAGE_HEIGHT, MARGIN_BOTTOM, MARGIN_TOP } from '@/guidetti/projectConstantes';
import { GFabricWorld } from '@/guidetti/GFabricWorld';
import checkFont from '@/utils/checkFont';
import { sortFields } from '@/utils/sortFields';
import { updateFontFamily } from '@/commons/fabricjs/FabricTextHelper';
import { Texture, SRGBColorSpace, MeshStandardMaterial } from 'three';
import { type Ref } from 'vue';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { FabricText } from 'fabric';
import type { IFont } from '@/typescript/interfaces/IFont';

function useMaterialUpdater(
    customMaterial: Ref<MeshStandardMaterial>,
    poleColor: Ref<string>,
    fonts: Ref<IFont[]>,
    scaleFactor: number,
    uniqueId: string,
): (fields: ITextField[] | IImageField[]) => Promise<void> {
    const imageUpdater = imageAndFabricWorldUpdater(poleColor, fonts, scaleFactor, true, uniqueId);
    const updaterInitial = imageUpdater([]);
    updaterInitial.then((update) => {
        if (!update) return;
        customMaterial.value.map = new Texture(update.canvas);
        customMaterial.value.map.colorSpace = SRGBColorSpace;
        customMaterial.value.map.flipY = false;
    });
    return (fields: ITextField[] | IImageField[]): Promise<void> => {
        const updater = imageUpdater(fields);
        return updater.then((update) => {
            if (!updater) return;
            customMaterial.value.map = new Texture(update!.canvas);
            customMaterial.value.map.colorSpace = SRGBColorSpace;
            customMaterial.value.map.flipY = false;
            customMaterial.value.needsUpdate = true;
            customMaterial.value.map!.needsUpdate = true;
        });
    };
}

function imageAndFabricWorldUpdater(
    poleColor: Ref<string>,
    fonts: Ref<IFont[]>,
    scaleFactor: number,
    smoothImages = true,
    uniqueId: string = '',
): (fields: ITextField[] | IImageField[] | []) => Promise<void | { canvas: HTMLCanvasElement; textureFabricWorld: GFabricWorld }> {
    const canvas = document.createElement('canvas');
    canvas.width = FINAL_IMAGE_WIDTH;
    canvas.height = FINAL_IMAGE_HEIGHT;
    const textureFabricWorld = new GFabricWorld();
    textureFabricWorld.init({
        canvas,
        enableRetinaScaling: true,
        includeDefaultValues: false, // do not export default values toJSON (shorter json)
        absolutePan: false,
        selection: false,
        allowTouchScrolling: true,
        allowTouchPan: true,
        backgroundColor: 'red',
        width: FINAL_IMAGE_WIDTH * scaleFactor,
        height: FINAL_IMAGE_HEIGHT * scaleFactor,
        smoothImages,
        uniqueId,
    });
    return (fields: ITextField[] | IImageField[] | []): Promise<void | { canvas: HTMLCanvasElement; textureFabricWorld: GFabricWorld }> => {
        textureFabricWorld.fabricCanvas!.clear();
        const promiseArray: Promise<void>[] = [];
        fields.forEach((field) => {
            if ((field as IImageField)?.url) {
                promiseArray.push(Promise.resolve());
            } else if ((field as ITextField)?.text ?? (field as ITextField)?.defaultText) {
                promiseArray.push(checkFont((field as ITextField).font, fonts.value));
            }
        });
        return Promise.all(promiseArray).then(() => {
            const fieldSorted = sortFields(fields);
            return textureFabricWorld.addMultipleFields(fieldSorted, false).then((fElements) => {
                fElements.forEach((fElem, index) => {
                    if (!fElem) return;
                    textureFabricWorld.fabricCanvas!.add(fElem);
                    fElem.left *= scaleFactor;
                    fElem.top *= scaleFactor;

                    if (fElem?.type === 'image') {
                        fElem.scaleX *= scaleFactor;
                        fElem.scaleY *= scaleFactor;
                        return;
                    }
                    updateFontFamily(
                        textureFabricWorld.fabricCanvas,
                        fElem as FabricText,
                        (fieldSorted[index] as ITextField).font,
                        // true,
                        false,
                    );
                });
                textureFabricWorld.displayRealSizeCanvas();
                textureFabricWorld.fabricCanvas!.renderAll();
                textureFabricWorld.addBackgroundColor(poleColor.value, scaleFactor);
                textureFabricWorld.fabricCanvas!.renderAll();
                // Canvas to texture

                return textureFabricWorld.stickerAndBrand(scaleFactor).then(() => {
                    return { canvas, textureFabricWorld };
                });
            });
        });
    };
}

function createThumbnailFromBat(params: {
    poleColor: Ref<string>;
    fonts: Ref<IFont[]>;
}): (fields: ITextField[] | IImageField[] | []) => Promise<Blob | Error> {
    const thumbnailWidth = 112 * 2;
    const thumbnailHeight = 160 * 2;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = thumbnailWidth;
    finalCanvas.height = thumbnailHeight;
    const ctx = finalCanvas.getContext('2d');
    const imgRender = imageAndFabricWorldUpdater(params.poleColor, params.fonts, 1, true);
    return (fields: ITextField[] | IImageField[] | []) => {
        const updateImg = imgRender(fields);
        return new Promise((resolve, reject) => {
            if (!ctx) {
                reject(new Error('No image can be generated for thumbnail'));
                return null;
            }
            return updateImg.then((render) => {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
                const reductImage = canvasReduction(render!.canvas, thumbnailWidth * 0.3, thumbnailHeight);
                const globalRatioFromReductionImage = reductImage.width / FINAL_IMAGE_WIDTH;
                const marginRatio = MARGIN_TOP * globalRatioFromReductionImage;
                const ratio = thumbnailHeight / (reductImage.height - (MARGIN_TOP + MARGIN_BOTTOM) * globalRatioFromReductionImage);
                const imgLeft = thumbnailWidth / 2 - (reductImage.width / 2) * ratio;
                const imgTop = -marginRatio;
                ctx.drawImage(reductImage, imgLeft, imgTop, reductImage.width * ratio, reductImage.height * ratio);
                return finalCanvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('No image can be generated for thumbnail'));
                            return null;
                        }
                        resolve(blob);
                        return blob;
                    },
                    'image/jpeg',
                    0.9,
                );
            });
        });
    };
}

/**
 * Allow the slow scale of an image, avoiding a brutal pixel compression with a smoother outcome
 * @param canvas
 * @param widthGoal
 * @param heightGoal
 * @enhance the calculation are sufficiente for it's purpose in this project
 * but could be improved working on the gap between goal target and actual canvas size
 * @returns
 */

function canvasReduction(canvas: HTMLCanvasElement, widthGoal?: number, heightGoal?: number, scalar: number = 0.75): HTMLCanvasElement {
    if (
        (!widthGoal && !heightGoal) ||
        (widthGoal && canvas.width * scalar <= widthGoal) ||
        (heightGoal && canvas.height * scalar <= heightGoal)
    ) {
        return canvas;
    }
    const { width, height } = canvas;
    const finalImageCanvas = document.createElement('canvas');
    const ctx = finalImageCanvas.getContext('2d');
    finalImageCanvas.width = Math.round(width * scalar);
    finalImageCanvas.height = Math.round(height * scalar);
    ctx?.drawImage(canvas, 0, 0, width, height, 0, 0, finalImageCanvas.width, finalImageCanvas.height);
    return canvasReduction(finalImageCanvas, widthGoal, heightGoal, scalar);
}

export { useMaterialUpdater, imageAndFabricWorldUpdater, createThumbnailFromBat, canvasReduction };
